import React from "react";

import cx from "classnames";

import { Button } from "../Button";
import { Icon } from "../Icon";
import type { IconDictionary } from "../Icon/types";

import styles from "./Notifications.scss";

export interface NotificationsProps {
  /**
   * Additional CSS class names to apply to the notifications component
   */
  className?: string;

  /**
   * Number of notifications to indicate
   * @default 0
   */
  count?: number;

  /**
   * Size of the notification icon
   * @default 26
   */
  size?: number;

  /**
   * Icon to display
   * @default "notification"
   */
  icon?: IconDictionary;

  /**
   * URL to navigate to when clicked
   */
  href?: string;

  /**
   * Handler for click events
   */
  onClick?: (event: React.MouseEvent) => unknown;

  /**
   * Whether the notification dropdown is open
   * @default false
   */
  isOpen?: boolean;

  /**
   * Test ID for the component
   */
  "data-testid"?: string;

  /**
   * Aria label for accessibility
   */
  "aria-label"?: string;
}

/**
 * A link-able icon that shows a numeric tag. Useful for notifications and
 * reminders.
 */
export function Notifications({
  className,
  count = 0,
  icon = "notification",
  size = 26,
  href,
  onClick,
  isOpen = false,
  "data-testid": testId,
  "aria-label": ariaLabel,
}: NotificationsProps) {
  const isLink = href || onClick;
  const spanTestId = isLink ? undefined : testId;
  const spanClass = cx(
    styles.notification,
    className && !isLink ? className : undefined
  );

  const iconWithNumber = (
    <div className={spanClass} data-testid={spanTestId}>
      <Icon icon={icon} size={size} color="currentColor" decorative />
      {count > 0 && <span className={styles.badge}>{count}</span>}
    </div>
  );

  if (!isLink) {
    return iconWithNumber;
  }

  return (
    <Button
      aria-label={ariaLabel}
      aria-expanded={isOpen}
      fullWidth={false}
      href={href}
      onClick={onClick}
      className={cx(className, styles.button)}
      data-testid={testId}
    >
      {iconWithNumber}
    </Button>
  );
}
